import { dedup, isNil } from '@gfe/core/utils/shared-utils';
import {
  fromSecondsToDuration,
  previousIsoDate,
  toISO8601Duration,
  toNaiveDateInTz,
  toNaiveTimeInTz
} from '@gfe/core/utils/time-utils';
import {
  CalendarEvent,
  CalendarEventModification
} from '@gfe/features/components/visualization/calendar/models/calendar.models';
import { VisuItemLabel } from '@gfe/features/components/visualization/common/models/label.model';
import {
  GaliaMarker,
  MarkerType
} from '@gfe/features/components/visualization/mapbox/models/galia-marker';

import { VisualizationModule } from '@core/models/data-visualization/data-visualization.model';
import { AuxiliaryDataBundle } from '@core/models/entities/entity-visualization-interface';
import { VisualizationBuilders } from '@core/models/entities/generic-entity';
import { EntityCfgOverrides } from '@core/models/entities/generic-entity-cfg';
import { GenericEntityRegistryService } from '@core/services/user-models-registry/generic-entity-registry.service';

import { ItemColorKey } from '../../colors.model';
import { Address } from './address';
import { CustomData } from './custom-data';
import { isOnceRecurrence } from './recurrence';
import {
  SiteSchedule,
  makeScheduleBackgroundColor,
  makeScheduleBorderColor,
  makeScheduleColor
} from './site-schedule';
import { DEFAULT_SCHEDULE_TIME_ZONE, recurringWindowTiming } from './utils';

type SiteKind =
  | 'Warehouse'
  | 'Hub'
  | 'Client'
  | 'VehicleBase'
  | 'Supplier'
  | { other: string };

export function isSiteKindCustom(kind: SiteKind): kind is { other: string } {
  return typeof kind === 'object' && kind !== null && 'other' in kind;
}

export function extractCustomKind(kind: SiteKind): string | undefined {
  return isSiteKindCustom(kind) ? kind.other : undefined;
}

export function siteKindToString(kind: SiteKind): string {
  if (isSiteKindCustom(kind)) {
    return kind.other;
  }
  return kind as string;
}

export interface UserInfrastructureAvailability {
  infrastructure_uid: string;
  quantity: number;
}

/**
 * The fields the occupation views read from a `UserInfrastructure` row. It reaches the
 * front end as auxiliary data rather than as an entity of its own.
 */
export interface UserInfrastructureV1Model {
  uid: string;
  name?: string;
}

export interface UserSiteV1Model {
  uid: string;
  kinds: SiteKind[];
  coords: { lat: number; lng: number };
  time_zone?: string;
  address?: Address;
  opening_schedules?: SiteSchedule[];
  before_service_setup: string;
  after_service_setdown: string;
  in_site_arriving_setup: string;
  in_site_leaving_setdown: string;
  out_site_arriving_setup: string;
  out_site_leaving_setdown: string;
  gln_code?: string;
  company_uid?: string;
  equipment_requirement_uids?: string[];
  infrastructure_availabilities?: UserInfrastructureAvailability[];
  color?: string;
  custom_data: CustomData;
}

function getSiteColor(site: UserSiteV1Model): ItemColorKey | string {
  if (!isNil(site.color)) {
    return site.color;
  }
  if (site.kinds.length !== 1) {
    return 'darkGrey';
  }
  const kind = site.kinds[0];
  switch (kind) {
    case 'Warehouse':
    case 'Hub':
    case 'VehicleBase':
      return 'lightGreen';
    case 'Client':
      return 'lightBlue';
    default:
      return 'darkGrey';
  }
}

function getSiteMarkerType(site: UserSiteV1Model): MarkerType {
  if (site.kinds.length !== 1) {
    return 'basic';
  }
  const kind = site.kinds[0];
  switch (kind) {
    case 'Warehouse':
    case 'Hub':
    case 'VehicleBase':
      return 'start';
    default:
      return 'basic';
  }
}

export function auxiliarySitesByUid(
  auxData?: AuxiliaryDataBundle
): Record<string, UserSiteV1Model> {
  return Object.fromEntries(
    ((auxData?.entitiesArrayPerVersionName['UserSite'] ?? []) as UserSiteV1Model[]).map(
      (site) => [site.uid, site]
    )
  );
}

export function makeSiteScheduleEvent(
  site: Pick<UserSiteV1Model, 'uid' | 'time_zone' | 'color'>,
  sch: SiteSchedule,
  auxiliarySites: Record<string, UserSiteV1Model>,
  editable = true
): CalendarEvent {
  const scheduleName = sch.name ?? sch.uid;

  const labels: VisuItemLabel[] = [];

  if (!isNil(sch.compatible_commodity_uids)) {
    labels.push({
      label: 'compatible_commodities',
      level: 0,
      value: sch.compatible_commodity_uids.join(', ')
    });
  }
  if (!isNil(sch.compatible_pickup_site_uids)) {
    labels.push({
      label: 'compatible_pickup_sites',
      level: 0,
      value: sch.compatible_pickup_site_uids.join(', ')
    });
  }
  if (!isNil(sch.compatible_job_kind_uids)) {
    labels.push({
      label: 'compatible_job_sites',
      level: 0,
      value: sch.compatible_job_kind_uids.join(', ')
    });
  }
  if (!isNil(sch.custom_data?.labels)) {
    Object.entries(sch.custom_data.labels).forEach(([key, value]) => {
      labels.push({ label: key, level: 0, value });
    });
  }

  return {
    uid: sch.uid,
    title: site.uid + ' - ' + scheduleName,
    ...recurringWindowTiming(sch.window, site.time_zone),
    mainColor: makeScheduleColor(sch),
    backgroundColor: makeScheduleBackgroundColor(sch, auxiliarySites, site.color),
    borderColor: makeScheduleBorderColor(sch),
    editable,
    description: `${scheduleName} schedule for site ${site.uid}`,
    labels
  };
}

/**
 * A dragged schedule is saved by PATCHing its site: an exception or a split appends a new
 * schedule, which only the site's `opening_schedules` array can carry, and a single array
 * element cannot be patched on its own.
 */
export function makeSiteScheduleModification(
  site: UserSiteV1Model,
  sch: SiteSchedule
): CalendarEventModification {
  const schedules = site.opening_schedules ?? [];
  const timeZone = site.time_zone ?? DEFAULT_SCHEDULE_TIME_ZONE;
  return {
    uid: sch.uid,
    entityType: 'site',
    entityTypeVersion: 'UserSite',
    withNewTimes: (start: Date, end: Date) => {
      const durationMs = Math.max(0, end.getTime() - start.getTime());
      const isOnce = isOnceRecurrence(sch.window.recurrence);
      const newWindow = {
        ...sch.window,
        time_window: {
          ...sch.window.time_window,
          start: toNaiveTimeInTz(start, timeZone),
          duration: toISO8601Duration(fromSecondsToDuration(durationMs / 1000))
        },
        // A one-off relocates to the drop day on a cross-day move; recurring
        // schedules keep their pattern (whole-series time shift).
        recurrence: isOnce
          ? { once: toNaiveDateInTz(start, timeZone) }
          : sch.window.recurrence
      };
      return {
        uid: site.uid,
        opening_schedules: schedules.map((s) =>
          s.uid === sch.uid ? { ...s, window: newWindow } : s
        )
      };
    },
    withException: (occurrenceDate: string, start: Date, end: Date) => {
      const durationMs = Math.max(0, end.getTime() - start.getTime());
      const newSchedule = {
        name: sch.name,
        kinds: sch.kinds,
        compatible_commodity_uids: sch.compatible_commodity_uids,
        compatible_job_kind_uids: sch.compatible_job_kind_uids,
        compatible_flow_kind_uids: sch.compatible_flow_kind_uids,
        compatible_pickup_site_uids: sch.compatible_pickup_site_uids,
        compatible_delivery_site_uids: sch.compatible_delivery_site_uids,
        custom_data: sch.custom_data,
        window: {
          time_window: {
            start: toNaiveTimeInTz(start, timeZone),
            duration: toISO8601Duration(fromSecondsToDuration(durationMs / 1000))
          },
          recurrence: { once: toNaiveDateInTz(start, timeZone) },
          exceptions: []
        }
      }; // uid omitted => backend-assigned
      return {
        uid: site.uid,
        opening_schedules: [
          ...schedules.map((s) =>
            s.uid === sch.uid
              ? {
                  ...s,
                  window: {
                    ...s.window,
                    exceptions: [...(s.window.exceptions ?? []), occurrenceDate]
                  }
                }
              : s
          ),
          newSchedule
        ]
      };
    },
    withFollowing: (occurrenceDate: string, start: Date, end: Date) => {
      const durationMs = Math.max(0, end.getTime() - start.getTime());
      // The dragged schedule is bounded to end the day before the split, so
      // earlier occurrences keep their original times.
      const truncatedValidTo = previousIsoDate(occurrenceDate);
      // A copy of the dragged schedule (same recurrence/config) takes over
      // from the split date onward, carrying the new times. Its uid is
      // omitted so the backend assigns a fresh one. It inherits the original
      // upper bound (null in the common open-ended case).
      const followingSchedule = {
        name: sch.name,
        kinds: sch.kinds,
        compatible_commodity_uids: sch.compatible_commodity_uids,
        compatible_job_kind_uids: sch.compatible_job_kind_uids,
        compatible_flow_kind_uids: sch.compatible_flow_kind_uids,
        compatible_pickup_site_uids: sch.compatible_pickup_site_uids,
        compatible_delivery_site_uids: sch.compatible_delivery_site_uids,
        custom_data: sch.custom_data,
        window: {
          time_window: {
            start: toNaiveTimeInTz(start, timeZone),
            duration: toISO8601Duration(fromSecondsToDuration(durationMs / 1000))
          },
          recurrence: sch.window.recurrence,
          exceptions: sch.window.exceptions ?? [],
          valid_from: occurrenceDate,
          valid_to: sch.window.valid_to ?? null
        }
      }; // uid omitted => backend-assigned
      return {
        uid: site.uid,
        opening_schedules: [
          ...schedules.map((s) =>
            s.uid === sch.uid
              ? { ...s, window: { ...s.window, valid_to: truncatedValidTo } }
              : s
          ),
          followingSchedule
        ]
      };
    }
  };
}

const siteCfgOverrides: EntityCfgOverrides<UserSiteV1Model> = {
  defaultSortingCol: ['name'],
  // The deployed schema does not declare the occupation module yet; offering it from
  // the front end keeps the visualizations the schema does declare.
  extraVisualizations: new Set<VisualizationModule>(['occupation']),
  availableDetailEntityType: ['site_schedules'],
  buildColumns: (autoColumns) => {
    return autoColumns.map((col) =>
      (col.key as string) === 'raw.kinds'
        ? {
            ...col,
            type: 'uid-list',
            renderHook(value) {
              const kinds = value as SiteKind[];
              return kinds.map(siteKindToString);
            }
          }
        : col
    );
  }
};

const siteVisualizationBuilders: VisualizationBuilders<UserSiteV1Model> = {
  buildMarkers: (raw: UserSiteV1Model): GaliaMarker[] => {
    const labels: VisuItemLabel[] = [];

    if (!isNil(raw.company_uid)) {
      labels.push({ label: 'company', level: 0, value: raw.company_uid });
    }

    const address = raw.address;
    if (!isNil(address) && !isNil(address.label)) {
      labels.push({ label: 'address', level: 0, value: address.label });
    }

    const schedules = raw.opening_schedules ?? [];
    labels.push({
      label: 'schedules',
      level: 0,
      value: dedup(schedules.map((sch) => sch.name)).join(', ')
    });

    labels.push({
      label: 'tags',
      level: 0,
      value: (raw.custom_data?.tags ?? []).join(', ')
    });

    Object.entries(raw.custom_data?.labels ?? {}).forEach(([key, value]) => {
      labels.push({ label: key, level: 1, value });
    });

    return [
      {
        coords: { lat: raw.coords.lat, lng: raw.coords.lng },
        color: getSiteColor(raw),
        type: getSiteMarkerType(raw),
        description: {
          name: raw.uid,
          startDate: undefined,
          endDate: undefined,
          content: labels
        },
        title: raw.uid,
        uid: raw.uid
      }
    ];
  },

  buildCalendarEvents: (
    raw: UserSiteV1Model,
    auxData?: AuxiliaryDataBundle
  ): CalendarEvent[] => {
    const auxiliarySites = auxiliarySitesByUid(auxData);
    return (raw.opening_schedules ?? []).map((sch) =>
      makeSiteScheduleEvent(raw, sch, auxiliarySites)
    );
  },

  buildCalendarModifications: (raw: UserSiteV1Model): CalendarEventModification[] =>
    (raw.opening_schedules ?? []).map((sch) => makeSiteScheduleModification(raw, sch)),

  occupationSiteUids: (raw: UserSiteV1Model): string[] => [raw.uid]
};

export function registerSiteExtensions(registry: GenericEntityRegistryService): void {
  registry.registerOverrides('UserSite', siteCfgOverrides);
  registry.registerVisualizationBuilders('UserSite', siteVisualizationBuilders);
}
